import React from 'react'
import { BaseView, BaseText } from '@/components/base'
import { RadioButton } from '@/components/RadioButton'
import { useTheme } from '@/hooks/useTheme'

export interface SelectCardItemProps {
  imagePath?: string
  title: string
  description: string
  isSelected?: boolean
  onPress?: () => void
  skeletonActive?: boolean
}

export const SelectCardItem: React.FC<SelectCardItemProps> = ({
  title,
  description,
  imagePath,
  onPress,
  skeletonActive,
  isSelected = false,
}) => {
  const theme = useTheme()

  if (skeletonActive) {
    return (
      <BaseView
        style={{
          height: '80px',
          backgroundColor: theme.colors.background.cardItemRoot,
          borderRadius: '24px',
          animation: 'pulse 1.5s ease-in-out infinite',
        }}
      />
    )
  }

  return (
    <BaseView
      style={{
        height: '80px',
        backgroundColor: theme.colors.background.cardItemRoot,
        borderRadius: '24px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '12px',
        cursor: 'pointer',
        transition: 'all 0.1s ease',
      }}
      className="hover:brightness-90 active:brightness-75"
      onClick={onPress}
    >
      {/* Avatar/Image */}
      <BaseView
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          overflow: 'hidden',
          backgroundColor: theme.colors.background.secondaryGray,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {imagePath ? (
          <img
            src={imagePath}
            alt={title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            onError={(e) => {
              // Fallback to placeholder if image fails to load
              e.currentTarget.style.display = 'none'
            }}
          />
        ) : (
          <BaseText fontSize={16} fontFamily="semibold" color="label.secondary">
            {title.charAt(0)}
          </BaseText>
        )}
      </BaseView>

      {/* Content */}
      <BaseView flex={1} gap={4}>
        <BaseText
          fontSize={16}
          fontFamily="semibold"
          numberOfLines={1}
          color="label.default"
        >
          {title}
        </BaseText>

        {description && (
          <BaseText
            fontSize={14}
            fontFamily="regular"
            color="label.description"
            numberOfLines={1}
          >
            {description}
          </BaseText>
        )}
      </BaseView>

      {/* Radio Button */}
      <RadioButton selected={isSelected} />
    </BaseView>
  )
}
